import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, RuntimeUiEventPayload } from '@blaze-runtime/engine-domain';
import { CanvasElementInputBinding } from '@blaze-shared/elements-domain';
import { SCHEMA_VALIDATION_ENABLED, syncSelect } from '@blaze-shared/platform-data-access';
import {
  BlazeSchema,
  createElementSchemaId,
  DATA_VALIDATION_EVENT_TYPE_EXCEPTIONS,
  isElementSchema,
  isElementSchemaId,
  validateData,
} from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { InputDataPackage } from '../canvas-elements/io-data/io-data.models';

import { ElementDefinitionsService } from './element-definitions.service';

export class DeclaredSchemaMismatchError extends Error {
  constructor(
    message: string,
    public declaredSchema: BlazeSchema,
  ) {
    super(message);
    this.name = 'DeclaredSchemaMismatchError';
  }
}

/** Incorrect type of the provided output schema */
export class OutputSchemaUnexpectedMissingSchemaError extends Error {}

/** Incorrect type of the provided output schema */
export class OutputSchemaNotElementSchemaError extends Error {}

/** Schema in the output definition is `null` - which means no data is expected but the schema in the event says otherwise */
export class OutputSchemaUnexpectedSchemaError extends Error {}

/** Schema in the output definition is `null` but the component emitted some data */
export class OutputSchemaNoDataExpected extends Error {}

/** The id of the schema is incorrect */
export class OutputSchemaIncorrectId extends Error {}

/** The elementId of the schema is incorrect */
export class OutputSchemaIncorrectElementId extends Error {}

/** The eventType of the schema is incorrect */
export class OutputSchemaIncorrectEventType extends Error {}

@Injectable({
  providedIn: 'root',
})
export class SchemaValidationService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly store = inject(Store);

  /** Set once per build — callers may skip assembling validation input when this is `false`. */
  readonly shouldValidate = inject(SCHEMA_VALIDATION_ENABLED);

  /** Validates the data that goes into the component */
  validateElementInputData(
    element: CanvasElement,
    inputName: CanvasElementInputBinding,
    valueResult: InputDataPackage,
    data: any,
  ): void {
    // Check if the validation should happen
    if (!this.shouldValidate) {
      return;
    }

    // Validate data against schema
    try {
      if (!valueResult.schema) {
        console.warn(
          `[Schema Validation] Schema not found for element (${element.name}, ${element.id}, ${element.type}), inputName (${inputName})`,
        );
      } else {
        validateData(valueResult.schema, data);
      }
    } catch (error) {
      console.error(
        `[Schema Validation] Input Schema Validation error, element (${element.name}, ${element.id}), inputName (${inputName})`,
        { error, schema: valueResult.schema, data },
      );
    }
  }

  /** Validates the data that goes out of the component and show the issues in the console */
  validateElementOutputEvent(element: CanvasElement, event: RuntimeUiEventPayload): void {
    try {
      this.performElementOutputEventValidation(element, event);
    } catch (error) {
      console.error(
        `[Schema Validation] Output Schema Validation error for (${element.name}, ${element.id}), event type: ${event.eventType}`,
        { error, schema: event?.schema, data: event?.data },
      );
    }
  }

  /** The actual element output validation method throwing the errors in case of a failure */
  performElementOutputEventValidation(element: CanvasElement, event: RuntimeUiEventPayload): void {
    // Check if the validation should happen
    if (!this.shouldValidate || DATA_VALIDATION_EVENT_TYPE_EXCEPTIONS.includes(event.eventType)) {
      return;
    }

    const outputDefinition = this.elementDefinitionsService
      .getIoConfig(element.type)
      ?.outputs?.find((output) => output.eventType === event.eventType);
    // If the schema in the definition is null, we're expecting to have no output (exception is the `query` event ignored at the top of this method)
    const expectingNoOutput = outputDefinition?.schema === null;

    // Warn if no schema present - in the future, we might want to enforce this
    if (!expectingNoOutput && !event.schema) {
      throw new OutputSchemaUnexpectedMissingSchemaError(
        `[Schema Validation] Output schema is missing for (${element.name}, ${element.id}), event type: ${event.eventType}`,
      );
    }

    // If the schema is null, we expect no data (used for trigger-like events: 'click', 'submit', etc.)
    if (expectingNoOutput) {
      if (event.schema !== null) {
        throw new OutputSchemaUnexpectedSchemaError(
          `[Schema Validation] Output definition schema is null, but the schema in the event is different for (${element.name}, ${element.id}), event type: ${event.eventType}`,
        );
      }

      if (event.data !== undefined) {
        throw new OutputSchemaNoDataExpected(
          `[Schema Validation] Output definition schema is null, but data is present for (${element.name}, ${element.id}), event type: ${event.eventType}`,
        );
      }

      // Otherwise - the schema (null) & data (undefined) match and no futher validation is needed
      return;
    }

    // The schema coming out of element's output should always be an ElementSchema
    if (!isElementSchema(event.schema)) {
      throw new OutputSchemaNotElementSchemaError(
        '[Schema Validation] Output schema is not an ElementSchema',
      );
    }

    // Validate the schema id
    if (
      !isElementSchemaId(event.schema.id) ||
      event.schema.id !== createElementSchemaId(element.id, event.eventType)
    ) {
      throw new OutputSchemaIncorrectId('[Schema Validation] Output schema - incorrect id');
    }

    // The schema's elementId should match the element id
    if (element.id !== event.schema.elementId) {
      throw new OutputSchemaIncorrectElementId(
        `[Schema Validation] Output schema - elementId mismatch (${element.name}, ${element.id} !== ${event.schema.elementId})`,
      );
    }

    // The schema's eventType should match the event type
    if (event.eventType !== event.schema.eventType) {
      throw new OutputSchemaIncorrectEventType(
        `[Schema Validation] Output schema - eventType mismatch (${element.name}, ${event.eventType} !== ${event.schema.eventType})`,
      );
    }

    // Compare previously declared schema with payload schema
    // Makes sure that the schema emitted with the data matches the schema previously declared.
    const schemaInState = syncSelect(
      this.store,
      runtimeSelectors.selectSchemaById(element.id, event.eventType),
    );
    // If the schema is missing - there's another mechanism checking for that (initial output schema validation)
    if (schemaInState && !isDeepEqual(schemaInState, event.schema)) {
      throw new DeclaredSchemaMismatchError(
        `[Schema Validation] Output schema mismatch for (${element.name}, ${element.id}), event type: ${event.eventType} - declared and emitted schema is different`,
        schemaInState,
      );
    }

    // Validate data
    validateData(event.schema, event.data);
  }
}
